import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X } from 'lucide-react';
import { 
  TextField, 
  SelectField, 
  NumberField, 
  CheckboxField, 
  DateTimeField, 
  TextareaField,
  InfoBox,
  FormButton,
  combineDateTimeFromFields
} from './FormFields';
import { formatDate, doTimeSlotsOverlap, debugDate } from '../utils/dateUtils';
import truckService from '../services/truckService';
import { usePermission } from '../../../../../context/PermissionContext';
import { handleApiError, formatPermissionError, isPermissionError } from '../../../../../utils/errorUtils';
import { getCurrentUserId, setFormSubmissionState } from '../../../../../utils/authUtils';
import { useAuth } from '../../../../../context/AuthContext';
import { useTimezone } from '../../../../../hooks/useTimezone';

// Debug flag for controlled logging
const DEBUG = process.env.NODE_ENV === 'development';

/**
 * Base component for truck scheduling forms
 * Provides common structure, validation and conflict checking
 */
const BaseTruckScheduleForm = ({
  onClose,
  onSave,
  preSelectedDate,
  isLoading,
  truckData,
  mode = 'inbound',
  initialValues,
  customFields = null,
  loadTypes = [],
  validateForm,
  title,
  businessUnitsOverride, // Optional override for business units list
  loadMaterialTypes = ['Pallet', 'Oversized Pallet', 'Gaylord', 'Small Parcel', 'Various'] // Default load material types
}) => {
  const today = new Date();
  const formattedToday = today.toISOString().split('T')[0];
  
  // Track form state - initialize with all fields from initialValues
  const [formData, setFormData] = useState(initialValues || {});
  const [errors, setErrors] = useState({});
  const [conflicts, setConflicts] = useState({
    hasTimeConflict: false,
    hasDockConflict: false,
    conflictingTrucks: []
  });
  
  
  // Register the setFormData function on window to enable child components to sync state
  // This is crucial for handling the PO field and other custom fields properly
  useEffect(() => {
    window.truckScheduleFormSetData = setFormData;
    
    // Cleanup function to remove the global reference when unmounted
    return () => {
      window.truckScheduleFormSetData = null;
    };
  }, []);
  
  // Use memo to log initial values only when they actually change
  // This helps reduce unnecessary console output
  useMemo(() => {
    if (DEBUG) console.log('BaseTruckScheduleForm initialValues:', initialValues);
  }, [initialValues]);
  
  // Load business units from API
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loadingBusinessUnits, setLoadingBusinessUnits] = useState(true);
  
  // Get user's timezone from our custom hook
  const userTimezone = useTimezone();
  const { currentUser } = useAuth();
  
  // For debugging - reduce this to a one-time log with useMemo
  useMemo(() => {
    if (DEBUG) console.log(`Using user timezone: ${userTimezone} for user: ${currentUser?.username || 'unknown'}`);
  }, [userTimezone, currentUser?.username]); 
  
  // Effect to update the scheduled date when preSelectedDate changes
  useEffect(() => {
    if (preSelectedDate) {
      setFormData(prev => ({
        ...prev,
        scheduled_date: preSelectedDate
      }));
    }
  }, [preSelectedDate]);
  
  // Load business units from API or use override
  // Move the fetch function outside of useEffect to avoid recreating it on every render
  const fetchBusinessUnits = useCallback(async () => {
    try {
      setLoadingBusinessUnits(true);

      // Use businessUnitsOverride if provided
      if (businessUnitsOverride) {
        // Only log once per instance with a unique property
        if (DEBUG && !window.__businessUnitsOverrideLogged) {
          console.log('Using overridden business units:', businessUnitsOverride);
          window.__businessUnitsOverrideLogged = true;
        }
        
        setBusinessUnits(businessUnitsOverride);

        // Dispatch a custom event to notify child components that business units are loaded
        const businessUnitsLoadedEvent = new CustomEvent('businessUnitsLoaded', {
          detail: businessUnitsOverride
        });
        window.dispatchEvent(businessUnitsLoadedEvent);
        return;
      }

      // Fetch from API if no override
      const response = await truckService.getAll('business-units');

      // Ensure consistent format for business units
      let formattedUnits = [];
      if (Array.isArray(response)) {
        // Map the response to ensure consistent ID/name format
        formattedUnits = response.map(unit => {
          // Ensure each unit has an id and name property
          return {
            id: unit.id || unit.business_unit_id || unit.value || unit,
            name: unit.name || unit.business_unit_name || unit.label || unit
          };
        });

        setBusinessUnits(formattedUnits);
        if (DEBUG) console.log('Business units loaded:', formattedUnits);
      } else if (response && response.data && Array.isArray(response.data)) {
        // Handle nested data structure if present
        formattedUnits = response.data.map(unit => ({
          id: unit.id || unit.business_unit_id || unit.value || unit,
          name: unit.name || unit.business_unit_name || unit.label || unit
        }));

        setBusinessUnits(formattedUnits);
        if (DEBUG) console.log('Business units loaded from nested data:', formattedUnits);
      }

      // Make business units available globally for child components
      window.businessUnits = formattedUnits;

      // Dispatch a custom event to notify child components that business units are loaded
      const businessUnitsLoadedEvent = new CustomEvent('businessUnitsLoaded', {
        detail: formattedUnits
      });
      window.dispatchEvent(businessUnitsLoadedEvent);
    } catch (error) {
      console.error('Error fetching business units:', error);
      // Fallback to default units if API call fails
      const fallbackUnits = [
        { id: '1', name: 'Electronics' },
        { id: '2', name: 'Furniture' },
        { id: '3', name: 'Apparel' },
        { id: '4', name: 'Automotive' }
      ];

      setBusinessUnits(fallbackUnits);
      if (DEBUG) console.log('Using fallback business units:', fallbackUnits);

      // Make fallback business units available globally for child components
      window.businessUnits = fallbackUnits;

      // Dispatch a custom event for the fallback units too
      const businessUnitsLoadedEvent = new CustomEvent('businessUnitsLoaded', {
        detail: fallbackUnits
      });
      window.dispatchEvent(businessUnitsLoadedEvent);
    } finally {
      setLoadingBusinessUnits(false);
    }
  }, [businessUnitsOverride]); // Only recreate if businessUnitsOverride changes

  // Use a ref to track if we've run the effect before
  useEffect(() => {
    fetchBusinessUnits();
    
    // Clean up flag when component unmounts
    return () => {
      window.__businessUnitsOverrideLogged = false;
    };
  }, [fetchBusinessUnits]); // Depend on the memoized fetch function
  
  // Memoize the checkConflicts function to prevent recreation on each render
  const checkConflicts = useCallback((data) => {
    // Skip if missing required fields
    if (!data.scheduled_date || !data.scheduled_time || !data.dock_door) {
      return;
    }
    
    // Create a Date object from the scheduled date and time
    const scheduledDateTime = combineDateTimeFromFields(data.scheduled_date, data.scheduled_time);
    if (!scheduledDateTime) return;
    
    // Check for conflicts
    const conflictingTrucks = truckData?.filter(truck => {
      // Skip the current truck being edited
      if (data.id && truck.id === data.id) return false;
      
      // Check if the dock door is the same
      const sameDockDoor = truck.dock_door === data.dock_door;
      if (!sameDockDoor) return false;
      
      // Check if the time periods overlap
      return doTimeSlotsOverlap(
        scheduledDateTime, 
        null, // End time not provided in this simple example
        new Date(truck.scheduled_at),
        null // End time not provided in this simple example
      );
    }) || [];
    
    setConflicts({
      hasTimeConflict: conflictingTrucks.length > 0,
      hasDockConflict: conflictingTrucks.length > 0,
      conflictingTrucks
    });
  }, [truckData]); // Only recreate when truckData changes
  
  // Memoize the handleChange function to prevent recreation on each render
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    // Log change event for debugging - only for business_unit and only once
    if (DEBUG && name === 'business_unit' && !window.__businessUnitChangeLogged) {
      console.log('Business Unit change:', {
        value,
        type: typeof value,
        valueIsObject: typeof value === 'object',
      });
      window.__businessUnitChangeLogged = true;
    }
    
    // Handle different field types
    let newValue;
    if (type === 'checkbox') {
      newValue = checked;
    } else {
      newValue = value;
    }
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    // Check for scheduling conflicts when changing date, time or dock door
    if (['scheduled_date', 'scheduled_time', 'dock_door'].includes(name)) {
      checkConflicts({
        ...formData,
        [name]: newValue
      });
    }
  }, [errors, formData, checkConflicts]); // Include dependencies
  
  // Process and submit form data - Enhanced to handle permission state
  // Use useCallback to memoize this function
  const forceDirectSubmit = useCallback(async () => {
    // Set the form submission state as early as possible to prevent redirects
    setFormSubmissionState(true);
    
    try {
      // Clear any previous general errors
      setErrors(prev => ({ ...prev, general: null }));
      
      // Reduce logging to minimum required
      if (DEBUG && !window.__formSubmissionLogged) {
        console.log('Form submission - mode:', {
          mode
        });
        window.__formSubmissionLogged = true;
      }
      
      // Ensure business_unit is correctly formatted (as a string/number, not an object)
      let validatedFormData = { ...formData };
      
      // If business_unit is an object with an id property, use that id
      if (typeof validatedFormData.business_unit === 'object' && 
          validatedFormData.business_unit !== null && 
          validatedFormData.business_unit.id) {
        validatedFormData.business_unit = validatedFormData.business_unit.id;
      }
      
      // Custom validation function from parent component
      const validationErrors = validateForm ? validateForm(validatedFormData) : {};
      
      // Check if there are validation errors
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        
        // Add a general error message
        setErrors(prev => ({
          ...prev,
          general: 'Please correct the validation errors highlighted below.'
        }));
        
        // Important: Don't forget to reset submission state on validation failure
        setTimeout(() => setFormSubmissionState(false), 500);
        return false;
      }
      
      // Update form data with validated values
      setFormData(validatedFormData);
      
      // Log for debugging - ensure load_material is included
      console.log('Form submission - validated data:', {
        ...validatedFormData,
        load_material: validatedFormData.load_material || ''
      });
      
      // Create a new truck object from the validated form data using user's timezone
      const scheduledDateTime = combineDateTimeFromFields(
        validatedFormData.scheduled_date, 
        validatedFormData.scheduled_time,
        userTimezone // Pass user's timezone for proper conversion to UTC
      );
      
      if (!scheduledDateTime) {
        setErrors(prev => ({
          ...prev,
          scheduled_date: 'Invalid date format',
          scheduled_time: 'Invalid time format',
          general: 'Please provide a valid date and time.'
        }));
        
        // Important: Don't forget to reset submission state on validation failure
        setTimeout(() => setFormSubmissionState(false), 500);
        return false;
      }
      
      // Get user information for the created_by field using our centralized utility
      const userId = getCurrentUserId();
      
      // Prepare truck data for API
      const newTruck = {
        ...validatedFormData,
        scheduled_at: scheduledDateTime.toISOString(),
        status: 'Scheduled',
        created_by: userId,         // Add created_by field automatically
        updated_by: userId,         // Add updated_by field automatically
        load_material: validatedFormData.load_material || '' // Explicitly ensure load_material is included
      };
      
      // Remove form-specific fields that shouldn't be sent to API
      delete newTruck.scheduled_date;
      delete newTruck.scheduled_time;
      
      // Send the data to the parent component for saving
      try {
        const result = await onSave(newTruck);
        
        // Special handling for outbound form errors
        if (mode === 'outbound' && result === false) {
          return false;
        }
        
        // Check if the result is an object with validation errors
        if (result && typeof result === 'object' && result.errors) {
          setErrors(prev => ({
            ...prev,
            ...result.errors
          }));
          return false;
        }
        
        // If successful, close the form
        if (result === true || (result && !result.errors)) {
          // Reset submission state before closing
          setFormSubmissionState(false);
          onClose();
          return true;
        } else {
          // Reset submission state on error
          setFormSubmissionState(false);
          
          setErrors(prev => ({
            ...prev,
            general: 'The server could not process your request. Please try again.'
          }));
          return false;
        }
      } catch (apiError) {
        // Use our error utilities to handle API errors
        const errorResult = handleApiError(apiError);
        
        // Check if this is a permission-related error
        if (errorResult.isPermissionError) {
          const permissionErrorMessage = `Permission denied: You need the 'truck_scheduler' permission to schedule ${mode} trucks. Please contact your administrator to request access.`;
          
          setErrors(prev => ({
            ...prev,
            general: permissionErrorMessage
          }));
        } 
        // Check if this is an authentication error
        else if (errorResult.isAuthError) {
          setErrors(prev => ({
            ...prev,
            general: 'Your session has expired. Please log in again.'
          }));
          
          // Optionally redirect to login after a short delay
          setTimeout(() => {
            window.location.href = '/login';
          }, 3000);
        }
        // Handle field validation errors
        else if (Object.keys(errorResult.fieldErrors).length > 0) {
          setErrors(prev => ({
            ...prev,
            ...errorResult.fieldErrors,
            general: errorResult.message || 'Please correct the errors below'
          }));
        } 
        // Generic error
        else {
          setErrors(prev => ({
            ...prev,
            general: errorResult.message || 'An unexpected error occurred during truck scheduling. Please try again or contact support.'
          }));
        }
        return false;
      } finally {
        // Reset submission state to re-enable redirects with a small delay
        // to ensure response is fully processed
        setTimeout(() => {
          setFormSubmissionState(false);
        }, 500);
      }
    } catch (error) {
      const errorResult = handleApiError(error);
      
      setErrors(prev => ({
        ...prev,
        general: errorResult.message || `An unexpected error occurred during form submission`
      }));
      
      // Reset submission state in case of errors with a short delay
      // to ensure any ongoing operations are complete
      setTimeout(() => {
        setFormSubmissionState(false);
      }, 500);
      
      return false;
    }
  }, [
    formData, 
    mode, 
    onClose, 
    onSave, 
    userTimezone, 
    validateForm,
    getCurrentUserId
  ]); // Include only safe dependencies - removing hasPermission which causes initialization errors

  // Track local loading state
  const [localLoading, setLocalLoading] = useState(false);
  const effectiveLoading = isLoading || localLoading;
  
  // Add enhanced debugging and error tracking - fixed to prevent infinite click events
  useEffect(() => {
    // Add global error handler
    const errorHandler = (event) => {
      if (DEBUG) {
        console.error('DEBUG: GLOBAL ERROR:', event.error);
        console.error('DEBUG: Error message:', event.error?.message);
        console.error('DEBUG: Error stack:', event.error?.stack);
        console.error('DEBUG: Error type:', event.error?.constructor?.name);
      } else {
        // In production, log minimal error info
        console.error('Form error:', event.error?.message);
      }
    };
    
    // Add unhandled promise rejection handler
    const rejectionHandler = (event) => {
      if (DEBUG) {
        console.error('DEBUG: UNHANDLED PROMISE REJECTION:', event.reason);
        console.error('DEBUG: Reason message:', event.reason?.message);
        console.error('DEBUG: Reason stack:', event.reason?.stack);
      } else {
        // In production, log minimal rejection info
        console.error('Promise rejection:', event.reason?.message);
      }
    };
    
    // Register global error handlers
    window.addEventListener('error', errorHandler);
    window.addEventListener('unhandledrejection', rejectionHandler);
    
    // Development mode debugging only
    if (DEBUG) {
      // Set up button debug listener
      if (!window.__debugButtonListenerAdded) {
        setTimeout(() => {
          const submitBtn = document.querySelector('#submitBtn');
          if (submitBtn) {
            console.log('DEBUG: Submit button found, adding event listener');
            window.__debugButtonListenerAdded = true;
            
            submitBtn.addEventListener('click', (e) => {
              console.log('DEBUG: BUTTON CLICKED', e);
              console.log('DEBUG: FORM STATE AT CLICK:', formData);
            });
          } else {
            console.log('DEBUG: Submit button not found');
          }
        }, 1000);
      } else {
        console.log('DEBUG: Button listener already added, skipping');
      }
      
      // Set up form validity check
      setTimeout(() => {
        const form = document.querySelector('form#truckScheduleForm');
        if (form) {
          console.log('DEBUG: Form found, checking validity');
          
          const formElements = Array.from(form.elements).map(el => ({
            name: el.name,
            id: el.id,
            value: el.value,
            type: el.type,
            tagName: el.tagName,
            valid: el.validity ? el.validity.valid : 'N/A',
            validationMessage: el.validationMessage || 'N/A',
            required: el.required
          }));
          
          console.log('DEBUG: FORM VALIDITY:', {
            checkValidity: form.checkValidity(),
            noValidate: form.hasAttribute('novalidate'),
            elements: formElements
          });
        } else {
          console.log('DEBUG: Form not found');
        }
      }, 1000);
    }
    
    // Cleanup function to remove event listeners
    return () => {
      window.removeEventListener('error', errorHandler);
      window.removeEventListener('unhandledrejection', rejectionHandler);
      
      // Don't reset the debug flag on unmount, as this would lead to duplicated listeners
      // if the component is remounted. We're keeping track globally.
    };
  }, []); // Empty dependency array means this runs once after mount
  
  // Get permission context
  const { hasPermission, hasAnyPermission, userPermissions } = usePermission();
  // Track submission state to prevent multiple submissions and redirects
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Check appropriate permissions based on the current mode
  const checkPermissions = () => {
    // Use truck_scheduler as the primary and only permission for both inbound and outbound modes
    const hasTruckSchedulerPermission = hasPermission('truck_scheduler');
    
    // For both inbound and outbound modes, only 'truck_scheduler' permission is needed
    // This simplifies the permission model and removes inconsistencies
    return hasTruckSchedulerPermission;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }
    
    // Avoid duplicate submissions
    if (isSubmitting) {
      return;
    }
    
    // Check permissions before attempting submission
    const hasRequiredPermission = checkPermissions();
    
    // Enhanced diagnostic logging for permission checks - only in development
    if (DEBUG) {
      console.log('Truck Scheduler Permission Diagnostics:', {
        hasPermission: hasRequiredPermission,
        mode: mode,
        userPermissions: userPermissions,
        hasTruckSchedulerPermission: hasPermission('truck_scheduler')
      });
    }
    
    // If the user doesn't have permission, show an error and don't submit
    if (!hasRequiredPermission) {
      setErrors(prev => ({
        ...prev,
        general: formatPermissionError({
          isPermissionError: true,
          message: `You don't have the 'truck_scheduler' permission required to schedule ${mode} trucks. Please contact your administrator to request access.`
        })
      }));
      
      // Log the exact permission issue for debugging (both in dev and prod for security logging)
      console.error(`Permission denied: User lacks 'truck_scheduler' permission required for ${mode} truck scheduling`);
      return false;
    }
    
    try {
      setIsSubmitting(true);
      setLocalLoading(true);
      
      // Check for auth token before submission
      const token = localStorage.getItem('token');
      if (!token) {
        setErrors(prev => ({
          ...prev,
          general: 'Authentication error. Please log in again.'
        }));
        return false;
      }
      
      // Process the form submission
      const result = await forceDirectSubmit();
      
      if (result === true) {
        // Success - form will be closed by the forceDirectSubmit function
        return true;
      }
      
      // If we got here, something went wrong but didn't throw an error
      return false;
    } catch (error) {
      console.error('Form submission error:', error);
      const errorResult = handleApiError(error);
      
      setErrors(prev => ({
        ...prev,
        general: errorResult.message || 'An unexpected error occurred during submission'
      }));
      return false;
    } finally {
      // Set a short timeout to avoid flashing loading state for fast submissions
      setTimeout(() => {
        setLocalLoading(false);
        setIsSubmitting(false);
      }, 500);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center p-4 z-50 overflow-y-auto">
      <div className="bg-slate-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-slate-700 flex justify-between items-center sticky top-0 bg-slate-800 z-10">
          <h2 className="text-xl font-semibold text-slate-200">
            {title || `Schedule New ${mode === 'inbound' ? 'Inbound' : 'Outbound'} Truck`}
          </h2>
          <button 
            className="text-slate-400 hover:text-slate-200"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form 
          onSubmit={(e) => {
            if (DEBUG) {
              console.log('DEBUG: FORM SUBMIT EVENT CAPTURED - Enhanced for permission debugging', {
                target: e.target.id,
                eventPhase: e.eventPhase,
                defaultPrevented: e.defaultPrevented,
                bubbles: e.bubbles,
                cancelable: e.cancelable,
                timeStamp: e.timeStamp,
                // Add additional debugging info for permissions
                hasTruckSchedulerPermission: hasPermission('truck_scheduler'),
                userPermissions: userPermissions,
                formData: { ...formData } // Spread to create a new object for logging
              });
            }
            
            // Add form submission state flag to pause redirects
            setFormSubmissionState(true);
            
            // Continue with submission
            handleSubmit(e);
          }} 
          className="p-4" 
          id="truckScheduleForm"
          noValidate // Disable browser validation to ensure our validation runs
        >
          {/* Display general errors */}
          {errors.general && (
            <InfoBox type="error">
              <p className="font-medium">Error</p>
              <p>{errors.general}</p>
              {errors.general.includes('permission') && (
                <p className="mt-2 text-sm">
                  <strong>Note:</strong> Form submission requires the 'truck_scheduler' permission. 
                  This permission controls access to both inbound and outbound truck operations.
                </p>
              )}
            </InfoBox>
          )}
          
          {/* Display conflicts if any */}
          {conflicts.hasTimeConflict && (
            <InfoBox type="warning">
              <p className="font-medium">Scheduling Conflict Detected</p>
              <p>Dock door {formData.dock_door} is already scheduled for use on {formatDate(formData.scheduled_date)} at {formData.scheduled_time}.</p>
              <p className="mt-1">You can still save this schedule, but be aware of the conflict.</p>
            </InfoBox>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
            {/* Business Unit */}
            <SelectField
              label="Business Unit"
              id="business_unit"
              name="business_unit"
              value={typeof formData.business_unit === 'object' ? 
                     (formData.business_unit?.id || '') : 
                     (formData.business_unit || '')}
              onChange={handleChange}
              options={businessUnits}
              valueField="id"     // Ensure we're using the ID as the value
              labelField="name"   // Display the name to the user
              error={errors.business_unit}
              required
              disabled={isLoading || loadingBusinessUnits}
            />
            
            {/* Carrier */}
            <TextField
              label="Carrier"
              id="carrier"
              name="carrier"
              value={formData.carrier || ''}
              onChange={handleChange}
              placeholder="Enter carrier name"
              error={errors.carrier}
              required
              disabled={isLoading}
            />
            
            {/* Load Type */}
            <SelectField
              label="Load Type"
              id="load_type"
              name="load_type"
              value={formData.load_type || ''}
              onChange={handleChange}
              options={loadTypes}
              error={errors.load_type}
              required
              disabled={isLoading}
            />
            
            {/* Load Material */}
            <SelectField
              label="Load Material"
              id="load_material"
              name="load_material"
              value={formData.load_material || ''}
              onChange={(e) => {
                handleChange(e);
                // Log for debugging
                console.log(`Load material selected: ${e.target.value}`);
              }}
              options={loadMaterialTypes}
              error={errors.load_material}
              required={false}
              disabled={isLoading}
            />
            
            {/* Size */}
            <NumberField
              label="Size (Number of Load Material)"
              id="size"
              name="size"
              value={formData.size || ''}
              onChange={handleChange}
              min={1}
              max={100}
              error={errors.size}
              required
              disabled={isLoading}
            />
            
            {/* Dock Door */}
            <TextField
              label="Dock Door"
              id="dock_door"
              name="dock_door"
              value={formData.dock_door || ''}
              onChange={handleChange}
              placeholder="e.g. D1, D2, etc."
              error={errors.dock_door}
              disabled={isLoading}
            />
          
            {/* Scheduled Date & Time */}
            <DateTimeField
              dateLabel="Scheduled Date"
              dateId="scheduled_date"
              dateName="scheduled_date"
              dateValue={formData.scheduled_date || formattedToday}
              timeLabel="Scheduled Time"
              timeId="scheduled_time"
              timeName="scheduled_time"
              timeValue={formData.scheduled_time || '09:00'}
              onChange={handleChange}
              dateError={errors.scheduled_date}
              timeError={errors.scheduled_time}
              required
              disabled={isLoading}
              minDate={formattedToday}
            />
            
            {/* Secure Shipment */}
            <div className="md:col-span-2">
              <CheckboxField
                label="Secure Shipment (requires additional security measures)"
                id="secure"
                name="secure"
                checked={formData.secure || false}
                onChange={handleChange}
                error={errors.secure}
                disabled={isLoading}
              />
            </div>
            
            {/* Custom Fields */}
            <div className={customFields && typeof customFields !== 'string' ? "md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-4" : "md:col-span-2"}>
              {customFields}
            </div>
            
            {/* Notes are now added on the truck record itself after creation
                via the multi-note thread, so the creation form no longer has
                a notes field. */}
          </div>
          
          {/* Form Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-md"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button 
              type="submit"
              id="submitBtn"
              className={`px-4 py-2 rounded-md font-medium ${mode === 'inbound' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white ${effectiveLoading || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''} flex items-center justify-center`}
              disabled={effectiveLoading || isSubmitting}
              onClick={(e) => {
                // Prevent double clicks
                if (isSubmitting) {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }
                
                // Debug log for PO field specifically - only in dev mode
                if (DEBUG && mode === 'inbound') {
                  console.log('Submit button PO field check:', {
                    formDataPO: formData.po,
                    formDataPOType: typeof formData.po,
                    formDataPOEmpty: !formData.po || formData.po === '',
                    documentPOValue: document.getElementById('po')?.value || 'Not found in DOM',
                    allFormDataKeys: Object.keys(formData)
                  });
                }
              }}
            >
              {effectiveLoading || isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  Schedule Truck
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BaseTruckScheduleForm;